import { DailyReportRow, PreviousDayStock, Signatory, ChemicalType } from '../types';
import { CHEMICALS } from '../constants/chemicals';
import { formatReportTitle, formatThaiDate } from './thaiDate';

interface ExportWordOptions {
  chemical: ChemicalType | 'all';
  rows: DailyReportRow[];
  previousDayStock: PreviousDayStock;
  startDate: string;
  endDate: string;
  signatories: Signatory[];
}

/**
 * โหลด mwa_logo.png แล้วแปลงเป็น Base64 Data URL เพื่อฝังลงในเอกสาร Word
 */
async function getLogoBase64(): Promise<string> {
  try {
    const response = await fetch('/mwa_logo.png');
    if (!response.ok) return '';
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('Could not load logo for Word export', err);
    return '';
  }
}

export async function exportReportToWord({
  chemical,
  rows,
  previousDayStock,
  startDate,
  endDate,
  signatories,
}: ExportWordOptions) {
  const targetChem: ChemicalType = chemical === 'all' ? 'alum' : chemical;
  const chemMeta = CHEMICALS[targetChem];
  const chemTitleName = `${chemMeta.nameTh} (${chemMeta.nameEn})`;
  const reportTitle = formatReportTitle(startDate, endDate, chemTitleName);

  const logoBase64 = await getLogoBase64();

  const formatNum = (val: number | null | undefined, decimals = 3) => {
    if (val === null || val === undefined || isNaN(val)) return '-';
    return Number(val).toLocaleString('th-TH', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // Calculations
  const prevStock = previousDayStock[targetChem] ?? 0;
  const totalIncome = rows.reduce((sum, r) => sum + (r[targetChem].income ?? 0), 0);
  const totalUsage = rows.reduce((sum, r) => sum + (r[targetChem].usage ?? 0), 0);
  const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
  const closingStock = lastRow ? (lastRow[targetChem].stock ?? 0) : 0;

  // Signatories setup (ensure 4 items: ผู้จัดทำ, ผู้รายงาน, ผู้ตรวจสอบ, ผู้อนุมัติ)
  const defaultRoles = ['ผู้จัดทำ', 'ผู้รายงาน', 'ผู้ตรวจสอบ', 'ผู้อนุมัติ'];
  const sigs = [0, 1, 2, 3].map((idx) => {
    const s = signatories[idx];
    return {
      roleTitle: (s?.roleTitle && s.roleTitle !== 'ผู้จัดทำ / ผู้รายงาน') ? s.roleTitle : defaultRoles[idx],
      name: s?.name || '',
      position: s?.position || '',
    };
  });

  const tableDailyRowsHtml = rows
    .map((r) => {
      const dayData = r[targetChem];
      const incomeVal = (dayData.income ?? 0) > 0 ? `+${formatNum(dayData.income)}` : formatNum(dayData.income);
      const usageVal = (dayData.usage ?? 0) > 0 ? formatNum(dayData.usage) : formatNum(dayData.usage);
      const stockVal = formatNum(dayData.stock);
      const dateDisplay = formatThaiDate(r.date, 'numeric');

      return `
        <tr>
          <td style="text-align: center; padding: 6px 8px; border: 1px solid #333333;">${dateDisplay}</td>
          <td style="text-align: right; padding: 6px 8px; border: 1px solid #333333;">${incomeVal}</td>
          <td style="text-align: right; padding: 6px 8px; border: 1px solid #333333;">${usageVal}</td>
          <td style="text-align: right; padding: 6px 8px; border: 1px solid #333333; font-weight: bold;">${stockVal}</td>
        </tr>
      `;
    })
    .join('');

  const prevDayDisplay = previousDayStock.date ? formatThaiDate(previousDayStock.date, 'numeric') : 'วันก่อนหน้า';

  const docHtml = `
    <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
    <head>
      <meta charset="utf-8">
      <title>${reportTitle}</title>
      <!--[if gte mso 9]>
      <xml>
        <w:WordDocument>
          <w:View>Print</w:View>
          <w:Zoom>100</w:Zoom>
          <w:DoNotOptimizeForBrowser/>
        </w:WordDocument>
      </xml>
      <![endif]-->
      <style>
        @page {
          size: A4 portrait;
          margin: 20mm 15mm 20mm 15mm;
          mso-header-margin: 10mm;
          mso-footer-margin: 10mm;
        }
        body {
          font-family: 'TH SarabunPSK', 'TH Sarabun New', 'Sarabun', Tahoma, sans-serif;
          font-size: 16pt;
          line-height: 1.25;
          color: #000000;
        }
        .header-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 16px;
          border: none;
        }
        .header-table td {
          border: none;
          vertical-align: middle;
        }
        .header-title {
          font-size: 18pt;
          font-weight: bold;
          margin-bottom: 3px;
          line-height: 1.25;
        }
        .header-subtitle {
          font-size: 14pt;
          color: #333333;
        }
        .section-title {
          font-size: 15pt;
          font-weight: bold;
          margin-top: 14px;
          margin-bottom: 6px;
        }
        table.data-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 14px;
          font-size: 15pt;
        }
        table.data-table th {
          background-color: #f2f4f7;
          font-weight: bold;
          text-align: center;
          padding: 6px 8px;
          border: 1px solid #333333;
        }
        table.data-table td {
          padding: 6px 8px;
          border: 1px solid #333333;
        }
        .sign-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 25px;
          border: none;
        }
        .sign-table td {
          width: 25%;
          text-align: center;
          vertical-align: top;
          padding: 0 4px;
          border: none;
          font-size: 13pt;
        }
      </style>
    </head>
    <body>
      <!-- ส่วนที่ 1: ตราสัญลักษณ์ mwa_logo.png และชื่อเรื่อง -->
      <table class="header-table">
        <tr>
          ${
            logoBase64
              ? `<td style="width: 75px; text-align: center; padding-right: 12px;">
                  <img src="${logoBase64}" width="70" height="70" alt="MWA Logo" style="width: 70px; height: 70px;" />
                </td>`
              : ''
          }
          <td style="text-align: ${logoBase64 ? 'left' : 'center'};">
            <div class="header-title">${reportTitle}</div>
            <div class="header-subtitle">โรงงานผลิตน้ำมหาสวัสดิ์ กองผลิตและจ่ายน้ำ ฝ่ายโรงงานผลิตน้ำมหาสวัสดิ์ การประปานครหลวง</div>
          </td>
        </tr>
      </table>

      <!-- ส่วนที่ 2: ตารางสรุป 2 แถว 4 คอลัมน์ -->
      <div class="section-title">สรุปภาพรวมตามช่วงเวลาที่เลือก (หน่วย: ตัน)</div>
      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 25%;">ยอดยกมา (ตัน)</th>
            <th style="width: 25%;">ปริมาณการรับ (ตัน)</th>
            <th style="width: 25%;">ปริมาณการใช้ (ตัน)</th>
            <th style="width: 25%;">ปริมาณคงเหลือ (ตัน)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="text-align: right; font-weight: bold;">${formatNum(prevStock)}</td>
            <td style="text-align: right; font-weight: bold; color: #047857;">+${formatNum(totalIncome)}</td>
            <td style="text-align: right; font-weight: bold; color: #b91c1c;">${formatNum(totalUsage)}</td>
            <td style="text-align: right; font-weight: bold; color: #1d4ed8;">${formatNum(closingStock)}</td>
          </tr>
        </tbody>
      </table>

      <!-- ส่วนที่ 3: ตารางแสดงข้อมูลรายวัน -->
      <div class="section-title">ตารางแสดงข้อมูลรายวัน (หน่วย: ตัน)</div>
      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 28%;">วันที่</th>
            <th style="width: 24%;">ปริมาณการรับ (ตัน)</th>
            <th style="width: 24%;">ปริมาณการใช้ (ตัน)</th>
            <th style="width: 24%;">ปริมาณคงเหลือ (ตัน)</th>
          </tr>
        </thead>
        <tbody>
          <!-- แถวแรก: ยอดยกมาของปริมาณคงเหลือ (ตัน) วันก่อนหน้า -->
          <tr style="background-color: #fffbeb;">
            <td style="text-align: center; font-weight: bold;">${prevDayDisplay} (ยอดยกมา)</td>
            <td style="text-align: center; color: #666666;">-</td>
            <td style="text-align: center; color: #666666;">-</td>
            <td style="text-align: right; font-weight: bold;">${formatNum(prevStock)}</td>
          </tr>
          ${tableDailyRowsHtml}
        </tbody>
        <tfoot>
          <tr style="background-color: #f1f5f9; font-weight: bold;">
            <td style="text-align: center; border: 1px solid #333333;">รวมทั้งสิ้น (${rows.length} วัน)</td>
            <td style="text-align: right; border: 1px solid #333333; color: #047857;">+${formatNum(totalIncome)}</td>
            <td style="text-align: right; border: 1px solid #333333; color: #b91c1c;">${formatNum(totalUsage)}</td>
            <td style="text-align: right; border: 1px solid #333333; color: #1d4ed8;">${formatNum(closingStock)}</td>
          </tr>
        </tfoot>
      </table>

      <!-- ส่วนที่ 4: การลงนาม 4 คน -->
      <table class="sign-table">
        <tr>
          ${sigs
            .map(
              (s, idx) => `
            <td>
              <div style="margin-bottom: 35px;">
                ลงชื่อ...................................................<br>
                <span style="font-size: 11pt; color: #555;">(${s.roleTitle || defaultRoles[idx]})</span>
              </div>
              <div>(${s.name ? s.name : '...................................................'})</div>
              <div>ตำแหน่ง ${s.position ? s.position : '...................................................'}</div>
              <div style="margin-top: 4px;">วันที่ ......./......./.......</div>
            </td>
          `
            )
            .join('')}
        </tr>
      </table>
    </body>
    </html>
  `;

  const blob = new Blob(['\ufeff', docHtml], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const fileName = `รายงาน_${chemMeta ? chemMeta.nameEn : 'สารเคมี'}_${startDate}_ถึง_${endDate}.docx`;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
